import { useCallback, useEffect, useMemo, useState } from "react";
import { Api, ApiError, type AgentSummary } from "../api.js";
import { Badge } from "../components/ui.js";
import { AnalyticsClient, saveFile, type Attribution, type ConvDetail, type ConvFilters, type ConvRow, type Day, type Format, type Question, type Range, type SourcesReport, type Summary } from "./client.js";
import { useAnalyticsText, type AText } from "./text.js";

/** Routes of this feature; App.tsx calls these three helpers and nothing else. */
export type AnalyticsRoute = { name: "statistics" } | { name: "conversations" } | { name: "conversation"; id: string };

export function parseAnalyticsRoute(path: string): AnalyticsRoute | null {
  if (path === "/statistics") return { name: "statistics" };
  if (path === "/conversations") return { name: "conversations" };
  const m = /^\/conversations\/([\w-]{1,64})$/.exec(path);
  return m ? { name: "conversation", id: m[1] as string } : null;
}

export function AnalyticsNav({ route }: { route: { name: string } }) {
  const { t } = useAnalyticsText();
  return (
    <>
      <a href="#/statistics" aria-current={route.name === "statistics" ? "page" : undefined}>
        {t("nav.statistics")}
      </a>
      <a href="#/conversations" aria-current={route.name === "conversations" || route.name === "conversation" ? "page" : undefined}>
        {t("nav.conversations")}
      </a>
    </>
  );
}

export function AnalyticsScreen({ api, route }: { api: Api; route: AnalyticsRoute }) {
  const client = useMemo(() => new AnalyticsClient(api), [api]);
  if (route.name === "statistics") return <Statistics client={client} api={api} />;
  if (route.name === "conversations") return <Conversations client={client} api={api} />;
  return <ConversationView key={route.id} client={client} id={route.id} />;
}

const day = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => day(new Date(Date.now() - n * 86_400_000));
const pct = (v: number | null) => (v === null ? "–" : `${Math.round(v * 1000) / 10}%`);
const minutes = (secs: number) => String(Math.round((secs / 60) * 10) / 10);

function useAgents(api: Api): AgentSummary[] {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  useEffect(() => {
    api.listAgents().then(setAgents, () => {});
  }, [api]);
  return agents;
}

function errorMessage(t: AText, e: unknown, fallback: Parameters<AText>[0]): string {
  if (e instanceof ApiError && e.status === 403) return t("conv.forbidden");
  return e instanceof ApiError && e.message && e.code !== "network" ? e.message : t(fallback);
}

function exportMessage(t: AText, e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 422) return t("export.tooLarge");
    if (e.status === 429) return t("export.limited");
    if (e.status === 503) return t("export.serverBusy");
    if (e.status === 403) return t("export.forbidden");
  }
  return t("export.failed");
}

/** Three buttons (CSV, PDF, Markdown) for one thing to download. */
function ExportButtons({ label, run }: { label: string; run: (format: Format) => Promise<{ blob: Blob; name: string }> }) {
  const { t } = useAnalyticsText();
  const [busy, setBusy] = useState<Format | null>(null);
  const [msg, setMsg] = useState<{ text: string; bad: boolean } | null>(null);
  const go = async (format: Format) => {
    setBusy(format);
    setMsg(null);
    try {
      const f = await run(format);
      saveFile(f.name, f.blob);
      setMsg({ text: t("export.done"), bad: false });
    } catch (e) {
      setMsg({ text: exportMessage(t, e), bad: true });
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="card" aria-label={label}>
      <h2>{label}</h2>
      <div className="row">
        {(["csv", "pdf", "md"] as Format[]).map((f) => (
          <button key={f} className="btn secondary" onClick={() => void go(f)} disabled={busy !== null}>
            {busy === f ? t("export.busy") : t(`export.${f}`)}
          </button>
        ))}
      </div>
      <p className={msg?.bad ? "error" : "hint"} role={msg?.bad ? "alert" : "status"}>
        {msg?.text ?? ""}
      </p>
    </section>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="stat">
      <dt>{label}</dt>
      <dd>{value}</dd>
      {hint && <dd className="hint">{hint}</dd>}
    </div>
  );
}

const RETENTION = [90, 180, 365, 730, 1095];

function Retention({ client }: { client: AnalyticsClient }) {
  const { t } = useAnalyticsText();
  const [days, setDays] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ text: string; bad: boolean } | null>(null);
  useEffect(() => {
    client.retention().then(setDays, () => {});
  }, [client]);
  const label = (n: number) => (n < 365 ? t("retention.months", { n: Math.round(n / 30) }) : n === 365 ? t("retention.year") : t("retention.years", { n: Math.round(n / 365) }));
  if (days === null) return null;
  const options = RETENTION.includes(days) ? RETENTION : [...RETENTION, days].sort((a, b) => a - b);
  return (
    <section className="card" aria-label={t("retention.title")}>
      <h2>{t("retention.title")}</h2>
      <p>{t("retention.help")}</p>
      <label className="inline">
        {t("retention.label")}{" "}
        <select
          value={days}
          onChange={async (e) => {
            const next = Number(e.target.value);
            try {
              await client.setRetention(next);
              setDays(next);
              setMsg({ text: t("retention.saved"), bad: false });
            } catch (err) {
              setMsg({ text: err instanceof ApiError && err.status === 403 ? t("retention.forbidden") : t("stats.error"), bad: true });
            }
          }}
        >
          {options.map((n) => (
            <option key={n} value={n}>
              {RETENTION.includes(n) ? label(n) : t("retention.days", { n })}
            </option>
          ))}
        </select>
      </label>
      <p className={msg?.bad ? "error" : "hint"} role={msg?.bad ? "alert" : "status"}>
        {msg?.text ?? ""}
      </p>
    </section>
  );
}

function DateRange({ range, onChange, agents }: { range: Range; onChange: (r: Range) => void; agents: AgentSummary[] }) {
  const { t } = useAnalyticsText();
  const preset = (n: number) => onChange({ ...range, from: daysAgo(n - 1), to: daysAgo(0) });
  return (
    <form className="row filters" onSubmit={(e) => e.preventDefault()} aria-label={t("filters.label")}>
      <label className="inline">
        {t("filters.agent")}{" "}
        <select aria-label={t("filters.agent")} value={range.agentId ?? ""} onChange={(e) => onChange({ ...range, agentId: e.target.value || undefined })}>
          <option value="">{t("filters.allAgents")}</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name || "…"}
            </option>
          ))}
        </select>
      </label>
      <label className="inline">
        {t("filters.from")} <input type="date" aria-label={t("filters.from")} value={range.from ?? ""} max={range.to} onChange={(e) => onChange({ ...range, from: e.target.value || undefined })} />
      </label>
      <label className="inline">
        {t("filters.to")} <input type="date" aria-label={t("filters.to")} value={range.to ?? ""} min={range.from} onChange={(e) => onChange({ ...range, to: e.target.value || undefined })} />
      </label>
      {[7, 30, 90].map((n) => (
        <button key={n} type="button" className="btn ghost" onClick={() => preset(n)}>
          {t(`filters.last${n}` as "filters.last7")}
        </button>
      ))}
    </form>
  );
}

/** Bars for the number of conversations per day. The table next to it carries the same numbers for screen readers. */
function Bars({ days }: { days: Day[] }) {
  const max = Math.max(1, ...days.map((d) => d.conversations));
  return (
    <div className="bars" aria-hidden="true" style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 80 }}>
      {days.map((d) => (
        <div key={d.day} title={`${d.day}: ${d.conversations}`} style={{ flex: 1, minWidth: 2, height: `${Math.max(4, (d.conversations / max) * 100)}%`, background: "currentColor", opacity: 0.55 }} />
      ))}
    </div>
  );
}

export function Statistics({ client, api }: { client: AnalyticsClient; api: Api }) {
  const { t } = useAnalyticsText();
  const agents = useAgents(api);
  const [range, setRange] = useState<Range>(() => ({ from: daysAgo(29), to: daysAgo(0) }));
  const [data, setData] = useState<{ summary: Summary; canRead: boolean; days: Day[]; top: Question[]; unanswered: Question[]; sources: SourcesReport | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    (async () => {
      try {
        const [s, days, sources] = await Promise.all([client.summary(range), client.daily(range), client.sources(range).catch(() => null)]);
        const q = s.canReadConversations ? await client.questions(range).catch(() => ({ top: [], unanswered: [] })) : { top: [], unanswered: [] };
        if (live) setData({ summary: s.summary, canRead: s.canReadConversations, days, top: q.top, unanswered: q.unanswered, sources });
      } catch (e) {
        if (live) setError(errorMessage(t, e, "stats.error"));
      }
    })();
    return () => {
      live = false;
    };
  }, [client, range, t]);

  const s = data?.summary;
  return (
    <main className="page" id="main">
      <h1>{t("stats.title")}</h1>
      <p>{t("stats.lead")}</p>
      <DateRange range={range} onChange={setRange} agents={agents} />
      {error && (
        <div className="banner warn" role="alert">
          {error}
        </div>
      )}
      {!data && !error && <p role="status">{t("stats.loading")}</p>}
      {data && s && (
        <>
          {!data.canRead && <div className="banner" role="note">{t("stats.numbersOnly")}</div>}
          <dl className="stats row" style={{ flexWrap: "wrap", gap: "1rem" }}>
            <Stat label={t("stats.conversations")} value={String(s.conversations)} />
            <Stat label={t("stats.avgMinutes")} value={String(s.avgMinutesPerConversation)} />
            <Stat label={t("stats.totalMinutes")} value={String(s.totalMinutes)} />
            <Stat label={t("stats.avgMessages")} value={String(s.avgMessagesPerConversation)} />
            <Stat label={t("stats.leads")} value={String(s.leads)} hint={s.leadRate === null ? undefined : pct(s.leadRate)} />
            <Stat label={t("stats.knowledge")} value={pct(s.answeredFromKnowledgeRate)} hint={s.answered ? t("stats.ofAnswers", { n: s.grounded, total: s.answered }) : t("stats.noAnswers")} />
            <Stat label={t("stats.unanswered")} value={pct(s.couldNotAnswerRate)} hint={s.answered ? t("stats.ofAnswers", { n: s.unanswered, total: s.answered }) : t("stats.noAnswers")} />
            <Stat label={t("stats.thumbs")} value={t("stats.thumbsValue", { up: s.thumbsUp, down: s.thumbsDown })} />
          </dl>
          <p className="hint">{t("stats.accuracyNote")}</p>

          <section className="card" aria-label={t("stats.perDay")}>
            <h2>{t("stats.perDay")}</h2>
            {data.days.length === 0 ? (
              <p>{t("stats.empty")}</p>
            ) : (
              <>
                <Bars days={data.days} />
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t("stats.day")}</th>
                      <th>{t("stats.conversations")}</th>
                      <th>{t("stats.messages")}</th>
                      <th>{t("stats.minutes")}</th>
                      <th>{t("stats.leads")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data.days].reverse().map((d) => (
                      <tr key={d.day}>
                        <td>{d.day}</td>
                        <td>{d.conversations}</td>
                        <td>{d.messages}</td>
                        <td>{minutes(d.activeSeconds)}</td>
                        <td>{d.leads}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>

          {data.sources && <SourcesSection report={data.sources} />}

          {data.canRead && (
            <>
              <QuestionTable title={t("stats.top")} rows={data.top} />
              <QuestionTable title={t("stats.unansweredList")} rows={data.unanswered} />
            </>
          )}
          <ExportButtons label={t("export.stats")} run={(f) => client.file({ kind: "stats", range }, f)} />
          <Retention client={client} />
        </>
      )}
    </main>
  );
}

const pct1 = (v: number | null) => (v === null ? "–" : `${Math.round(v * 1000) / 10}%`);

/** Words for a channel key, in the reader's language. */
function channelName(t: AText, channel: string): string {
  return t(`ch.${channel}` as never) || channel;
}

function CountTable({ head, rows }: { head: string[]; rows: (string | number)[][] }) {
  return (
    <table className="table">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td key={j}>{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Where conversations came from: numbers only, so every role sees it. */
function SourcesSection({ report }: { report: SourcesReport }) {
  const { t } = useAnalyticsText();
  const tail = [t("src.conversations"), t("src.leads"), t("src.leadRate")];
  const nums = (x: { conversations: number; leads: number; leadRate: number | null }) => [x.conversations, x.leads, pct1(x.leadRate)];
  return (
    <section className="card" aria-label={t("src.title")}>
      <h2>{t("src.title")}</h2>
      <p>{t("src.lead")}</p>
      {report.channels.length === 0 ? (
        <p>{t("src.empty")}</p>
      ) : (
        <>
          <h3>{t("src.channel")}</h3>
          <CountTable head={[t("src.channel"), ...tail]} rows={report.channels.map((x) => [channelName(t, x.channel), ...nums(x)])} />
          <h3>{t("src.sources")}</h3>
          <CountTable head={[t("src.source"), t("src.medium"), ...tail]} rows={report.sources.map((x) => [x.label || x.source || channelName(t, x.channel), x.medium, ...nums(x)])} />
          {report.campaigns.length > 0 && (
            <>
              <h3>{t("src.campaigns")}</h3>
              <CountTable head={[t("src.campaign"), t("src.source"), t("src.medium"), ...tail]} rows={report.campaigns.map((x) => [x.campaign, x.source, x.medium, ...nums(x)])} />
            </>
          )}
          {report.landingPages.length > 0 && (
            <>
              <h3>{t("src.landing")}</h3>
              <CountTable head={[t("src.page"), ...tail]} rows={report.landingPages.map((x) => [x.page, ...nums(x)])} />
            </>
          )}
          {report.submissionPages.length > 0 && (
            <>
              <h3>{t("src.submission")}</h3>
              <CountTable head={[t("src.page"), ...tail]} rows={report.submissionPages.map((x) => [x.page, ...nums(x)])} />
            </>
          )}
          <p className="hint">{t("src.note")}</p>
        </>
      )}
    </section>
  );
}

function QuestionTable({ title, rows }: { title: string; rows: Question[] }) {
  const { t } = useAnalyticsText();
  return (
    <section className="card" aria-label={title}>
      <h2>{title}</h2>
      {rows.length === 0 ? (
        <p>{t("stats.topEmpty")}</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t("stats.question")}</th>
              <th>{t("stats.times")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((q) => (
              <tr key={q.question}>
                <td>{q.question}</td>
                <td>{q.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

const PAGE = 25;
const when = (iso: string, lang: string) => new Date(iso).toLocaleString(lang, { dateStyle: "medium", timeStyle: "short" });

export function Conversations({ client, api }: { client: AnalyticsClient; api: Api }) {
  const { t, lang } = useAnalyticsText();
  const agents = useAgents(api);
  const [filters, setFilters] = useState<ConvFilters>({});
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<ConvRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The search box waits for a pause in typing before asking the server.
  useEffect(() => {
    const id = setTimeout(() => setFilters((f) => (f.q === (search.trim() || undefined) ? f : { ...f, q: search.trim() || undefined })), 350);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(
    async (offset: number) => {
      setBusy(true);
      try {
        const r = await client.conversations(filters, offset, PAGE);
        setItems((cur) => (offset === 0 ? r.conversations : [...(cur ?? []), ...r.conversations]));
        setTotal(r.total);
        setError(null);
      } catch (e) {
        setItems((cur) => cur ?? []);
        setError(errorMessage(t, e, "conv.error"));
      } finally {
        setBusy(false);
      }
    },
    [client, filters, t],
  );
  useEffect(() => {
    void load(0);
  }, [load]);

  const filtered = Boolean(filters.agentId || filters.from || filters.to || filters.q || filters.test);
  return (
    <main className="page" id="main">
      <h1>{t("conv.title")}</h1>
      <p>{t("conv.lead")}</p>
      {error && (
        <div className="banner warn" role="alert">
          {error}
        </div>
      )}
      <form className="row filters" onSubmit={(e) => e.preventDefault()} aria-label={t("filters.label")}>
        <label className="inline">
          {t("filters.agent")}{" "}
          <select aria-label={t("filters.agent")} value={filters.agentId ?? ""} onChange={(e) => setFilters((f) => ({ ...f, agentId: e.target.value || undefined }))}>
            <option value="">{t("filters.allAgents")}</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name || "…"}
              </option>
            ))}
          </select>
        </label>
        <label className="inline">
          {t("filters.from")} <input type="date" aria-label={t("filters.from")} value={filters.from ?? ""} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value || undefined }))} />
        </label>
        <label className="inline">
          {t("filters.to")} <input type="date" aria-label={t("filters.to")} value={filters.to ?? ""} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value || undefined }))} />
        </label>
        <input type="search" aria-label={t("filters.search")} placeholder={t("filters.searchPlaceholder")} value={search} maxLength={100} onChange={(e) => setSearch(e.target.value)} />
        <label className="inline">
          <input type="checkbox" checked={!!filters.test} onChange={(e) => setFilters((f) => ({ ...f, test: e.target.checked || undefined }))} /> {t("filters.showTests")}
        </label>
      </form>

      {items === null && !error && <p role="status">{t("stats.loading")}</p>}
      {items?.length === 0 && !error && (
        <div className="card empty">
          <p>{filtered ? t("conv.noMatch") : t("conv.none")}</p>
        </div>
      )}
      {!!items?.length && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>{t("conv.started")}</th>
                <th>{t("conv.firstQuestion")}</th>
                <th>{t("conv.agent")}</th>
                <th>{t("src.column")}</th>
                <th>{t("conv.messages")}</th>
                <th>{t("conv.minutes")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>{when(c.startedAt, lang)}</td>
                  <td>
                    <a href={`#/conversations/${c.id}`}>{c.firstQuestion || "…"}</a>{" "}
                    {c.isTest && <Badge tone="muted">{t("conv.test")}</Badge>} {c.hasLead && <Badge tone="ok">{t("conv.lead.badge")}</Badge>}
                  </td>
                  <td>{c.agentName || t("conv.deletedAgent")}</td>
                  <td>
                    {c.sourceLabel ?? t("ch.unknown_before")}
                    {c.campaign && <span className="hint"> · {c.campaign}</span>}
                  </td>
                  <td>{c.messages}</td>
                  <td>{minutes(c.activeSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row between">
            <span className="hint">{t("conv.showing", { n: items.length, total })}</span>
            {items.length < total && (
              <button className="btn secondary" onClick={() => void load(items.length)} disabled={busy}>
                {busy ? t("stats.loading") : t("conv.more")}
              </button>
            )}
          </div>
          <ExportButtons label={t("export.list")} run={(f) => client.file({ kind: "list", filters }, f)} />
        </>
      )}
    </main>
  );
}

/** How the visitor arrived. The addresses are visitor-controlled input, so they are shown as plain text, never as links. */
function HowArrived({ attribution: a, submissionUrl }: { attribution: Attribution | null; submissionUrl: string | null }) {
  const { t } = useAnalyticsText();
  const ids = a ? Object.entries(a.params).filter(([k]) => !k.startsWith("utm_")) : [];
  const rows: [string, string][] = a
    ? ([
        [t("src.sourceMedium"), `${a.label}${a.medium ? ` / ${a.medium}` : ""}`],
        [t("src.campaign"), a.campaign],
        [t("src.landingUrl"), a.landingUrl ?? ""],
        [t("src.conversationPage"), submissionUrl ?? ""],
        [t("src.cameFrom"), a.referrer ?? ""],
        [t("src.clickIds"), ids.map(([k, v]) => `${k}=${v}`).join("  ")],
      ] as [string, string][]).filter(([, v]) => v)
    : ([[t("src.conversationPage"), submissionUrl ?? ""]] as [string, string][]).filter(([, v]) => v);
  return (
    <section className="card" aria-label={t("src.how")}>
      <h2>{t("src.how")}</h2>
      {!a && <p>{t("ch.unknown_before")}</p>}
      <dl>
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd style={{ overflowWrap: "anywhere" }}>{v}</dd>
          </div>
        ))}
      </dl>
      {a && (
        <p className="hint">
          {t(`src.confidence.${a.confidence}` as never)} {a.firstSeenBy === "derived" ? t("src.derived") : t("src.firstSeenBy")}
        </p>
      )}
    </section>
  );
}

export function ConversationView({ client, id }: { client: AnalyticsClient; id: string }) {
  const { t, lang } = useAnalyticsText();
  const [conv, setConv] = useState<ConvDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [gone, setGone] = useState(false);

  useEffect(() => {
    client.conversation(id).then(setConv, (e) => setError(e instanceof ApiError && e.status === 404 ? t("conv.notFound") : errorMessage(t, e, "conv.error")));
  }, [client, id, t]);

  const modeText = (mode: string | null, status: string) =>
    status !== "complete" ? t("conv.answerFailed") : mode === "grounded" ? t("conv.answerKnowledge") : mode === "no_knowledge" || mode === "refusal" ? t("conv.answerNone") : t("conv.answerGeneral");

  if (gone)
    return (
      <main className="page" id="main">
        <p role="status">{t("conv.deleted")}</p>
        <a href="#/conversations">{t("conv.back")}</a>
      </main>
    );
  return (
    <main className="page" id="main">
      <p>
        <a href="#/conversations">← {t("conv.back")}</a>
      </p>
      {error && (
        <div className="banner warn" role="alert">
          {error}
        </div>
      )}
      {!conv && !error && <p role="status">{t("stats.loading")}</p>}
      {conv && (
        <>
          <h1>{when(conv.startedAt, lang)}</h1>
          <p className="hint">
            {t("conv.agent")}: {conv.agentName || t("conv.deletedAgent")} · {t("conv.channel")}: {conv.channel} · {t("conv.minutes")}: {minutes(conv.activeSeconds)} {conv.isTest && <Badge tone="muted">{t("conv.test")}</Badge>}
          </p>
          <HowArrived attribution={conv.attribution} submissionUrl={conv.submissionUrl} />
          <ol className="transcript" aria-label={t("conv.timeline")} style={{ listStyle: "none", padding: 0 }}>
            {conv.messageList.map((m, i) => (
              <li key={i} className="card" data-role={m.role}>
                <strong>{m.role === "user" ? t("conv.visitor") : t("conv.agentSays")}</strong>{" "}
                <span className="hint">{new Date(m.at).toLocaleTimeString(lang, { timeStyle: "short" })}</span>
                {/* Plain text on purpose: what a visitor wrote is never shown as markup. */}
                <p style={{ whiteSpace: "pre-wrap" }}>{m.content}</p>
                {m.role === "assistant" && (
                  <p className="hint">
                    <Badge tone={m.mode === "grounded" && m.status === "complete" ? "ok" : "muted"}>{modeText(m.mode, m.status)}</Badge>
                    {m.vote && (
                      <>
                        {" "}
                        <span role="img" aria-label={m.vote === "up" ? t("conv.thumbUp") : t("conv.thumbDown")}>
                          {m.vote === "up" ? "👍" : "👎"}
                        </span>
                      </>
                    )}
                  </p>
                )}
              </li>
            ))}
          </ol>
          <ExportButtons label={t("export.one")} run={(f) => client.file({ kind: "one", id }, f)} />
          <section className="card">
            {!confirming ? (
              <button className="btn ghost" onClick={() => setConfirming(true)}>
                {t("conv.delete")}
              </button>
            ) : (
              <div role="alertdialog" aria-label={t("conv.delete")}>
                <p>{t("conv.deleteConfirm")}</p>
                <button
                  className="btn danger"
                  onClick={async () => {
                    try {
                      await client.remove(id);
                      setGone(true);
                    } catch {
                      setError(t("conv.deleteFailed"));
                      setConfirming(false);
                    }
                  }}
                >
                  {t("conv.deleteYes")}
                </button>{" "}
                <button className="btn ghost" onClick={() => setConfirming(false)}>
                  {t("conv.cancel")}
                </button>
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
}
